import Phaser from 'phaser';
import {
  BOSS_IDS,
  CLASS_IDS,
  ENEMY_TYPE_IDS,
  type ClassId,
  type EnemyType,
} from '@gladiators/shared';

/**
 * Art: Kenney "Tiny Dungeon" (CC0) — https://kenney.nl/assets/tiny-dungeon
 * Shipped as `public/assets/kenney_tiny_dungeon.png` (the pack's
 * `Tilemap/tilemap_packed.png`, 12x11 grid of 16x16 tiles, no spacing).
 * Licence text: `public/assets/kenney_license.txt`.
 *
 * The pack's characters are single static tiles, so `walkSheet()` synthesises a
 * short 4-frame bob/lean cycle from each tile at load time — a "simple walk
 * animation" without hand-drawing frames.
 */

export const SHEET_KEY = 'kenney';
export const TILE = 16;

/** Frame indices into the Kenney sheet (row-major, 12 per row). */
export const TILES = {
  groundBase: 48,
  groundVariants: [48, 49, 50, 51],
  wall: 40,
  rock: 58,
  goblin: 109,
} as const;

/** Which Kenney character tile represents each class, plus a colour accent. */
export const CLASS_TILE: Record<ClassId, number> = {
  warrior: 96, // knight in full plate
  wizard: 84, // purple mage
  ranger: 112, // green-hooded archer
  cleric: 97, // armoured holy fighter
  druid: 88, // brown-robed hooded figure
  warlock: 85, // pale-robed caster
};

export const CLASS_TINT: Record<ClassId, number> = {
  warrior: 0xff6a6a,
  wizard: 0x8fb0ff,
  ranger: 0x7ff0a0,
  cleric: 0xffe89a,
  druid: 0x9be86a,
  warlock: 0xc79bff,
};

/**
 * feature 5.1a — per enemy-type Kenney frame + tint. `goblin` keeps its
 * original frame / tint. The other frames are sensible-ish picks from the Tiny
 * Dungeon character rows; the distinct tint is what actually makes each type
 * read at a glance (the debuff tints in `syncEnemies` still override it).
 */
export const ENEMY_FRAME: Record<EnemyType, number> = {
  goblin: 109,
  goblinBrute: 111,
  ratSwarm: 123,
  impBomber: 124,
  // feature 5.1b
  goblinArcher: 110,
  goblinShaman: 108,
  spiderSpitter: 122,
  skeletonKnight: 100,
  witchHound: 121,
};

export const ENEMY_TINT: Record<EnemyType, number> = {
  goblin: 0x8fd06a,
  goblinBrute: 0xd0894a,
  ratSwarm: 0xb9b3a6,
  impBomber: 0xff5a3c,
  // feature 5.1b — a distinct tint per type is what makes each read at a glance
  goblinArcher: 0x9acd5a,
  goblinShaman: 0x5ad0c0,
  spiderSpitter: 0x8a5ad0,
  skeletonKnight: 0xdfe0e6,
  witchHound: 0xb03ad0,
};

/** The enemy sprite-sheet texture key for an etype (built in `buildTextures`). */
export function enemySheet(etype: string): string {
  return `enemy-${etype}`;
}

/**
 * feature 5.2 — per boss-id Kenney frame + tint. The boss is drawn at a much
 * larger scale in `syncEnemies`; the deep-red tint + size is what makes it read.
 */
export const BOSS_FRAME: Record<string, number> = {
  warlord: 111,
  brambleMatron: 123,
  frostRevenant: 116,
  galeTwins: 117,
  furnaceGolem: 112,
  arenaMaster: 100,
};
export const BOSS_TINT: Record<string, number> = {
  warlord: 0xc0392b,
  brambleMatron: 0x3f9b46,
  frostRevenant: 0x5db4e6,
  galeTwins: 0x9fd8cf,
  furnaceGolem: 0xe8702a,
  arenaMaster: 0xf2c14e,
};

/** The boss sprite-sheet texture key for a boss id (built in `buildTextures`). */
export function bossSheet(bossId: string): string {
  return `boss-${bossId}`;
}

/** Back-compat alias — the goblin tint (unchanged from the pilot). */
export const GOBLIN_TINT = ENEMY_TINT.goblin;

const WALK_FRAMES = 4;
const BOB = [0, 1, 2, 1];
const LEAN = [0, 0.08, 0, -0.08];

function sourceFrame(
  scene: Phaser.Scene,
  index: number,
): { img: CanvasImageSource; x: number; y: number } {
  const tex = scene.textures.get(SHEET_KEY);
  const frame = tex.get(index);
  return {
    img: tex.getSourceImage() as CanvasImageSource,
    x: frame.cutX,
    y: frame.cutY,
  };
}

function walkSheet(scene: Phaser.Scene, key: string, tileIndex: number): void {
  if (scene.textures.exists(key)) scene.textures.remove(key);
  const { img, x, y } = sourceFrame(scene, tileIndex);

  const fw = TILE;
  const fh = TILE + 3; // headroom for the bob
  const canvas = document.createElement('canvas');
  canvas.width = fw * WALK_FRAMES;
  canvas.height = fh;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  for (let f = 0; f < WALK_FRAMES; f++) {
    ctx.save();
    ctx.translate(f * fw + fw / 2, 3 - BOB[f]!);
    ctx.transform(1, 0, LEAN[f]!, 1, 0, 0);
    ctx.drawImage(img, x, y, TILE, TILE, -fw / 2, 0, TILE, TILE);
    ctx.restore();
  }

  const tex = scene.textures.addCanvas(key, canvas)!;
  for (let f = 0; f < WALK_FRAMES; f++) tex.add(f, 0, f * fw, 0, fw, fh);

  scene.anims.create({
    key: `${key}-walk`,
    frames: scene.anims.generateFrameNumbers(key, { start: 0, end: WALK_FRAMES - 1 }),
    frameRate: 9,
    repeat: -1,
  });
  scene.anims.create({ key: `${key}-idle`, frames: [{ key, frame: 0 }], frameRate: 1 });
}

/** Call from a scene's `create()` — the Kenney sheet must already be loaded. */
export function buildTextures(scene: Phaser.Scene): void {
  for (const cls of CLASS_IDS as ClassId[]) {
    walkSheet(scene, `char-${cls}`, CLASS_TILE[cls]);
  }
  for (const etype of ENEMY_TYPE_IDS as EnemyType[]) {
    walkSheet(scene, enemySheet(etype), ENEMY_FRAME[etype]);
  }
  for (const bossId of BOSS_IDS) {
    walkSheet(scene, bossSheet(bossId), BOSS_FRAME[bossId] ?? TILES.goblin);
  }
}
